import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const AREAS = ["Verkauf", "Ankauf", "Werkstatt", "Disposition", "Warenwirtschaft", "Buchhaltung"];

/**
 * Jun-2026 (User-Wunsch) — Aufgabe aus „Was zu tun ist" einem Arbeitsbereich
 * + Mitarbeiter zuweisen. Die Aufgabe landet im Mitarbeiter-Dashboard.
 */
export default function AssignTaskDialog({ open, onClose, item, onAssigned }) {
    const [employees, setEmployees] = useState([]);
    const [area, setArea] = useState("");
    const [assigneeId, setAssigneeId] = useState("");
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        const t = setTimeout(() => {
            setArea("");
            setAssigneeId("");
            setLoading(true);
            api.get("/dealer/employees")
                .then((r) => {
                    if (cancelled) return;
                    const list = (r.data.items || []).filter((e) => e.has_login !== false);
                    setEmployees(list);
                    // Schnell-Zuweisung: aus der Mitarbeiter-Auslastung vorgeschlagenen
                    // Mitarbeiter vorauswählen, falls vorhanden.
                    try {
                        const pref = localStorage.getItem("tw_quick_assign_assignee");
                        if (pref && list.some((e) => e.id === pref)) setAssigneeId(pref);
                    } catch { /* ignore */ }
                })
                .catch(() => { if (!cancelled) setEmployees([]); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, 0);
        return () => { cancelled = true; clearTimeout(t); };
    }, [open]);

    const empName = (e) => e.name || [e.first_name, e.last_name].filter(Boolean).join(" ") || e.email || "Mitarbeiter";

    const submit = async () => {
        if (!area) { toast.error("Bitte einen Bereich wählen"); return; }
        if (!assigneeId) { toast.error("Bitte einen Mitarbeiter wählen"); return; }
        setSaving(true);
        try {
            await api.post("/dealer/tasks", {
                area,
                assignee_id: assigneeId,
                title: item?.title || "Aufgabe",
                body: item?.body || "",
                note: note.trim() || null,
                source_type: item?.type || null,
                cta_url: item?.cta_url || null,
            });
            toast.success("Aufgabe zugewiesen");
            try { localStorage.removeItem("tw_quick_assign_assignee"); } catch { /* ignore */ }
            onAssigned?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuweisen fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="assign-task-dialog">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#FFCC00]/20 text-[#111827]">
                            <UserPlus className="w-5 h-5" />
                        </span>
                        <DialogTitle className="font-display font-black text-lg">Aufgabe zuweisen</DialogTitle>
                    </div>
                    <DialogDescription className="text-sm text-gray-700 mt-1 truncate">
                        {item?.title}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Bereich</label>
                        <select
                            value={area}
                            onChange={(e) => setArea(e.target.value)}
                            className="w-full mt-1 rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm font-bold"
                            data-testid="assign-task-area"
                        >
                            <option value="">— Bereich wählen —</option>
                            {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Mitarbeiter</label>
                        {loading ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Lade Mitarbeiter…</div>
                        ) : employees.length === 0 ? (
                            <p className="text-sm text-gray-500 py-2" data-testid="assign-task-no-employees">
                                Noch keine Login-Mitarbeiter angelegt. Lege sie in den Einstellungen → Mitarbeiter an.
                            </p>
                        ) : (
                            <select
                                value={assigneeId}
                                onChange={(e) => setAssigneeId(e.target.value)}
                                className="w-full mt-1 rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm font-bold"
                                data-testid="assign-task-assignee"
                            >
                                <option value="">— Mitarbeiter wählen —</option>
                                {employees.map((e) => <option key={e.id} value={e.id}>{empName(e)}</option>)}
                            </select>
                        )}
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Notiz (optional)</label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            placeholder="z. B. Bitte bis Freitag erledigen …"
                            className="w-full mt-1 rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm resize-none"
                            data-testid="assign-task-note"
                        />
                    </div>
                    <p className="text-[11px] text-gray-400">
                        Wird der Auftrag nicht innerhalb von 3 Tagen bearbeitet, erscheint er als „nicht bearbeitet“ wieder hier.
                    </p>
                </div>

                <DialogFooter className="flex sm:justify-between gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} data-testid="assign-task-cancel">Abbrechen</Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={saving || employees.length === 0}
                        className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm uppercase tracking-wider"
                        data-testid="assign-task-submit"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Zuweisen"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
